import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title:
    "Talking to Your Psychiatrist About TMS | Neuro Wellness TMS Centers of America",
  description:
    "How to discuss TMS and SAINT with your current psychiatrist or prescriber. What to say, what to bring, and what to ask.",
  canonical: "/talking-to-your-psychiatrist",
  keywords:
    "talking to psychiatrist about TMS, how to discuss TMS with doctor, TMS referral, SAINT discussion with prescriber",
};

const relatedLinks = [
  { title: "What Is TMS?", href: "/contents/tms/what-is-tms-therapy" },
  { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
  {
    title: "What to Expect From Your Consultation",
    href: "/what-to-expect-from-your-consultation",
  },
  { title: "Schedule a Consultation", href: "/contactus" },
];

export default function TalkingToYourPsychiatrist() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="For Your Provider"
      title="Talking to Your Psychiatrist About TMS"
      subtitle="Your current prescriber is an important part of this conversation. Here's how to bring it up and what to ask."
    >
      <DecisionSection heading="Why your prescriber matters">
        <p>
          Your psychiatrist or prescriber knows your medication history, your
          response to different treatments, and your overall clinical picture.
          Their involvement is important — both for coordinating your care and
          because insurance authorization often requires documentation from your
          prescriber.
        </p>
      </DecisionSection>

      <DecisionSection heading="How to bring it up">
        <p>You don't need to make a case. You can simply say:</p>
        <p className="italic pl-4 border-l-2 border-accent">
          "I've been reading about TMS and SAINT for treatment-resistant
          depression. I'd like to understand whether it might be an option for
          me. Can we talk about it?"
        </p>
        <p>
          If your prescriber isn't familiar with SAINT specifically, you can
          share that it's an FDA-cleared, accelerated TMS protocol developed at
          Stanford. Our team is also available to speak with your prescriber
          directly if that would help.
        </p>
      </DecisionSection>

      <DecisionSection heading="What to bring to the conversation">
        <DecisionList
          items={[
            "A list of medications you've tried, with dates and outcomes",
            "Your current symptoms and how they're affecting your daily life",
            "Any questions about TMS or SAINT you have",
            "Our phone number — (954) 827-9793 — if your prescriber wants to speak with us",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="What to ask">
        <DecisionList
          items={[
            "Do you think TMS might be appropriate for my situation?",
            "Would I need to change my current medications?",
            "Can you provide documentation of my medication history for insurance?",
            "Would you be willing to coordinate with the Neuro Wellness team?",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="If your prescriber is supportive">
        <p>
          Great — we'll work with them directly. We coordinate with your
          existing prescriber or therapist so your care stays connected. Your
          prescriber can provide the treatment summary and referral that
          insurance often requires.
        </p>
      </DecisionSection>

      <DecisionSection heading="If you don't have a prescriber">
        <p>
          If you're not currently under the care of a psychiatrist, we can help
          establish care. Our team includes board-certified psychiatrists and
          psychiatric nurse practitioners.{" "}
          <Link
            to="/services/medication-management"
            className="text-accent hover:underline"
          >
            Learn about our medication management services
          </Link>
          .
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link to="/contactus" className="text-accent hover:underline">
            schedule a consultation
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
